/**
 * DOCUMENTATION:
 * Component: SystemDesignDatabases
 * Purpose: Teaching database scaling through Replication and Sharding.
 * * * Note for Junior Developers:
 * - Replication (Read Scaling): Copying data from a 'Leader' to 'Followers'.
 * You write to the leader, but read from the followers.
 * - Sharding (Write Scaling): Splitting one giant table into multiple smaller
 * tables across different servers (e.g., Users A-M on Server 1, N-Z on Server 2).
 * - Latency vs. Consistency: Replication often involves a "lag." If you write
 * and immediately read from a follower, you might see old data.
 */

import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack,
} from "@mui/material";

/* Icons */
import StorageIcon from '@mui/icons-material/Storage';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import SyncIcon from '@mui/icons-material/Sync';

const bgImage = "https://images.unsplash.com/photo-1451187580459-43490279c0fa";

/* Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(10, 5, 25, 0.9), rgba(10, 5, 25, 0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(20, 15, 45, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    color: "white",
}));

const FeatureBox = styled(Box)(({ theme, bordercolor }) => ({
    padding: theme.spacing(2),
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    border: `1px solid ${bordercolor || "rgba(0, 200, 255, 0.2)"}`,
    borderRadius: "10px",
    height: "100%",
}));

export default function SystemDesignDatabases() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "#00c8ff" }}>
                    SCALING DATA
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 1. Database Replication */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "#4ade80" }}><SyncIcon /></Avatar>
                            <Typography variant="h5" fontWeight={800}>Database Replication</Typography>
                        </Box>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                            Most web apps have 100x more reads than writes. <strong>Replication</strong> handles this by
                            having one "Leader" (Master) for writing data and multiple "Followers" (Slaves) for reading data.
                        </Typography>



                        <Grid container spacing={2} sx={{ mt: 3 }}>
                            <Grid item xs={12} md={6}>
                                <FeatureBox bordercolor="#4ade80">
                                    <Typography variant="subtitle1" fontWeight={700} color="#4ade80">High Availability</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                        If the Leader dies, one of the Followers can be promoted to Leader, keeping the app alive.
                                    </Typography>
                                </FeatureBox>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FeatureBox bordercolor="#ff6b6b">
                                    <Typography variant="subtitle1" fontWeight={700} color="#ff6b6b">Eventual Consistency</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                        Updates take time to reach followers. This is called "Replication Lag."
                                    </Typography>
                                </FeatureBox>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 2. Database Sharding */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "#00c8ff" }}><AltRouteIcon /></Avatar>
                            <Typography variant="h5" fontWeight={800}>Database Sharding</Typography>
                        </Box>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                            When your data becomes too large for one server's hard drive, you use <strong>Sharding</strong>.
                            This is horizontal partitioning—breaking one big table into many smaller ones based on a
                            "Shard Key" (like User ID).
                        </Typography>



                        <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px", mb: 3 }}>
                            <Typography variant="subtitle2" color="#00c8ff" gutterBottom>How it works (Mathematical Partitioning):</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                To find which server a user's data lives on, we use a hash function:
                            </Typography>
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                <Typography variant="h6" sx={{ fontFamily: 'serif' }}>
                                    $$ServerIndex = Hash(UserID) \pmod N$$
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                                    Where $N$ is the number of database servers.
                                </Typography>
                            </Box>
                        </Box>

                        <Stack spacing={2}>
                            <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderLeft: "4px solid #00c8ff", borderRadius: "4px" }}>
                                <Typography fontWeight={700}>Complexity Warning:</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    Once you shard, joining data between different servers becomes extremely difficult.
                                    Sharding should be your <strong>last resort</strong> after optimization and replication.
                                </Typography>
                            </Box>
                        </Stack>
                    </SectionCard>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/system-design-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#00c8ff", color: "#0a0519", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
